import { Link } from 'react-router';
import { buildMeta, breadcrumbSchema, faqSchema } from '~/lib/seo';
import { SITE } from '~/data/site';
import { shippedServices } from '~/data/services';

const SERVICES_FAQ = [
  {
    q: 'What is the difference between commercial cleaning and janitorial service?',
    a: 'In practice, janitorial is the recurring work — the same checklist run on a schedule, night after night. Commercial cleaning is the broader category and includes the periodic jobs: floor restoration, post-construction, exterior glass. Most buildings need a janitorial contract plus a few of the periodic services on a cycle.',
  },
  {
    q: 'Why do you only list five services when other companies list twenty?',
    a: 'Because we do these five and we can say exactly how. A long menu implies crews and equipment a company may not have, and one vague answer about strip-and-wax teaches a facility manager everything else was oversold too. Where a specialist would do a job better than us, we say so.',
  },
  {
    q: 'What does “on request” mean?',
    a: 'It means we quote that job individually rather than running it on a standing schedule — floor care, post-construction, windows and pressure washing. It is not a hedge. Nightly janitorial and disinfection are core: we do those ourselves, tonight, with the crew and kit we already have.',
  },
  {
    q: 'Can you combine several services into one contract?',
    a: 'Yes, and the quote itemises them. A nightly janitorial rate with quarterly window cleaning and an annual strip-and-wax is a normal arrangement. Seeing each line separately is what lets you drop or add one later without renegotiating everything.',
  },
] as const;

export function meta() {
  return buildMeta({
    title: 'Commercial cleaning services',
    description:
      'Nightly janitorial and disinfection with our own crew. Floor care, post-construction and window cleaning on request. What each covers, and its price.',
    path: '/services',
    jsonLd: [
      faqSchema(SERVICES_FAQ),
      breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Services', url: '/services' },
      ]),
    ],
  });
}

export default function Services() {
  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">Services</p>
            <h1>What we actually do.</h1>
            <p className="lede">
              Two we do ourselves with our own crew. Three we will quote for you, and if a
              specialist does them better we will tell you that instead of pretending.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="grid-2">
            {shippedServices().map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="card card-link">
                <h3 style={{ fontSize: 'var(--step-1)' }}>{s.name}</h3>
                <p className="small" style={{ fontStyle: 'italic' }}>
                  &ldquo;{s.worry}&rdquo;
                </p>
                <p>{s.blurb}</p>
                <span className="card-more">Read more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight">
        <div className="shell">
          <div className="faq">
            <h2>Questions about scope</h2>
            {SERVICES_FAQ.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Not sure which of these you need?</h2>
            <p>Most buildings need one recurring service and two on a cycle. We will tell you which.</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Get a quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
